import type { OnboardingRecommendationAnswers } from "@repo/types";
import {
  parseVmId,
  type PortfolioNetwork,
  type VmPreference,
} from "~/lib/auth/vm-types";

export const queryKeys = {
  platformApi: {
    all: ["platform-api"] as const,
    // One platform-api call, one key: appConfig.get powers both the
    // dashboard and investing-account surfaces. Keep this the only shape so
    // the persistence allowlist and realtime platform-api invalidation
    // always cover it.
    appConfig: ["platform-api", "appConfig.get"] as const,
    accumulatorList: ["platform-api", "v2.accumulator.list"] as const,
    accumulatorTiers: ["platform-api", "v2.accumulator.tiers"] as const,
    accumulatorSummaryById: (portfolioId: string) =>
      ["platform-api", "v2.accumulator.summary", portfolioId] as const,
    // One call, one key: the addon-registry boot loader is the only
    // consumer of addons.listInstalled. Keyed by USER id (never wallet):
    // the feed is user-owned and wallets vary across VMs.
    addonsListInstalledByUser: (userKey: string | null) =>
      ["platform-api", "addons.listInstalled", userKey] as const,
    activePromotions: [
      "platform-api",
      "multipliers.getActivePromotions",
    ] as const,
    // Prefix key covering every receiptsListByUser filter combination —
    // used by the activity page's feed-driven invalidation.
    receiptsListPrefix: ["platform-api", "v2.receipts.list"] as const,
    receiptById: (receiptId: string | null) =>
      ["platform-api", "v2.receipts.get", receiptId] as const,
    // Keyed by USER id (never wallet): receipts are user-owned actions
    // across every portfolio and VM.
    receiptsListByUser: (
      userKey: string | null,
      portfolioId: string | null,
      kinds: readonly string[] | null,
      states: readonly string[] | null,
    ) =>
      [
        "platform-api",
        "v2.receipts.list",
        userKey,
        portfolioId,
        kinds ? [...kinds].sort().join(",") : null,
        states ? [...states].sort().join(",") : null,
      ] as const,
    sharedReceiptBySlug: (slug: string | null) =>
      ["platform-api", "v2.receipts.shared.get", slug] as const,
    accountGasSavingsByAddress: (ownerAddress: string | null) =>
      [
        "platform-api",
        "strategyInstances.getAccountGasSavings",
        ownerAddress,
      ] as const,
    accountPerformanceSeriesByAddress: (
      accountAddress: string | null,
      returnMethod: string,
    ) =>
      [
        "platform-api",
        "strategyInstances.getAccountPerformanceSeries",
        accountAddress,
        returnMethod,
      ] as const,
    displayProfileByWallet: (walletAddress: string | null) =>
      [
        "platform-api",
        "displayPreferences.getDisplayProfile",
        walletAddress,
      ] as const,
    inviteCodeByVm: (vmPreference: VmPreference) =>
      ["platform-api", "inviteCodes.getInviteCode", vmPreference] as const,
    currentUserIdentity: ["platform-api", "identity.getCurrent"] as const,
    // Wallet-scoped via withAuthQueryScope; the resolved wallet is also
    // passed as the query input. Dashboard resolves it with the VM
    // preference, referrals with the default — cache entries are shared
    // only when both resolve the same wallet.
    pointsOverview: ["platform-api", "points.getPointsOverview"] as const,
    portfolioBuilderDraft: [
      "platform-api",
      "v2.portfolio.builder.getDraft",
    ] as const,
    portfolioBuilderProfile: [
      "platform-api",
      "v2.portfolio.builder.getProfile",
    ] as const,
    portfolioBuilderRecommend: (network: string, prompt: string) =>
      [
        "platform-api",
        "v2.portfolio.builder.recommend",
        network,
        prompt,
      ] as const,
    portfolioBuilderRecommendForOnboardingAnswers: (
      answers: OnboardingRecommendationAnswers,
    ) =>
      [
        "platform-api",
        "v2.portfolio.builder.recommendForOnboardingAnswers",
        ...answers.selfDescriptions,
        "interests",
        ...answers.interests,
        answers.interestsUnknown,
      ] as const,
    onboardingEntryIntent: [
      "platform-api",
      "v2.onboarding.resolveEntry",
    ] as const,
    preferencesEffectiveByKey: (preferenceKey: string) =>
      ["platform-api", "preferences.getEffective", preferenceKey] as const,
    // One call, one key: the notable-investor cards on the explore and
    // stocks pages share this Redis-cached, parameterless read.
    exploreStrategyCardReturns: [
      "platform-api",
      "strategyBlueprints.getExploreStrategyCardReturns",
    ] as const,
    strategyCampaignApr: (legacyBlueprintId: string | null) =>
      [
        "platform-api",
        "strategyBlueprints.getDynamicAprForStrategy",
        legacyBlueprintId,
      ] as const,
    strategyInstancesOwnedByAddress: (ownerAddress: string | null) =>
      [
        "platform-api",
        "strategyInstances.getStrategyInstancesOwnedByAddress",
        ownerAddress,
      ] as const,
    // Third segment is a status scope: a VM preference for VM-scoped reads
    // or "welcome" for the welcome-flow read. This user-owned status must be
    // auth-scoped at the factory so no consumer can create a shared key.
    userOnboardingStatus: (statusScope: string, authScope: AuthQueryScope) =>
      withAuthQueryScope(
        ["platform-api", "userLegacy.getOnboardingStatus", statusScope],
        authScope,
      ),
  },
  assetSelector: {
    all: ["asset-selector"] as const,
    search: (
      normalizedQuery: string,
      allowedSelectionKinds: readonly string[],
      availabilityFilter: string | null,
      chainFilter: readonly string[],
    ) =>
      [
        "asset-selector",
        "search",
        normalizedQuery,
        allowedSelectionKinds,
        availabilityFilter,
        chainFilter,
      ] as const,
    suggestions: (
      normalizedQuery: string,
      availabilityFilter: string | null,
      chainFilter: readonly string[],
    ) =>
      [
        "asset-selector",
        "suggestions",
        normalizedQuery,
        availabilityFilter,
        chainFilter,
      ] as const,
  },
  referrals: {
    all: ["referrals"] as const,
    inviteCodes: ["referrals", "invite-codes"] as const,
    rates: ["referrals", "rates"] as const,
  },
  operatingPreferences: {
    // Root segment predates the factory: the onboarding-v2 route keys the
    // operating-preference profile on it as-is.
    profile: ["operating-preference-profile"] as const,
  },
  commandPalette: {
    all: ["command-palette"] as const,
    assetSearchFast: (normalizedQuery: string) =>
      ["command-palette", "asset-search-fast", normalizedQuery] as const,
    assetSearchTokens: (normalizedQuery: string) =>
      ["command-palette", "asset-search-tokens", normalizedQuery] as const,
    assetSearchSuggest: (normalizedQuery: string) =>
      ["command-palette", "asset-search-suggest", normalizedQuery] as const,
    resolveAddress: (normalizedAddress: string, chainId: string | null) =>
      [
        "command-palette",
        "resolve-address",
        normalizedAddress,
        chainId,
      ] as const,
    onchainSearch: (normalizedQuery: string, limit: number) =>
      ["command-palette", "onchain-search", normalizedQuery, limit] as const,
  },
  featureFlags: {
    all: ["feature-flags"] as const,
    userFlags: ["feature-flags", "user-flags"] as const,
    globalFlagsByKeys: (featureKeys: readonly string[]) =>
      ["feature-flags", "global-flags", featureKeys] as const,
  },
  asset: {
    all: ["asset"] as const,
    details: ["asset", "details"] as const,
    detailsById: (assetId: string) => ["asset", "details", assetId] as const,
    lookup: ["asset", "lookup"] as const,
    lookupByToken: (chainId: string, tokenAddress: string) =>
      ["asset", "lookup", chainId, tokenAddress] as const,
    browseByIssuer: (issuerSlug: string, excludeAssetId: string) =>
      ["asset", "browse", "issuer", issuerSlug, excludeAssetId] as const,
    browseStocks: (excludeAssetId: string) =>
      ["asset", "browse", "stocks", excludeAssetId] as const,
  },
  template: {
    all: ["template"] as const,
    detailById: (blueprintId: string) =>
      ["template", "detail", blueprintId] as const,
  },
  onboarding: {
    all: ["onboarding"] as const,
    starterPortfolios: (network: PortfolioNetwork | null) =>
      ["onboarding", "starter-portfolios", network ?? "unsupported"] as const,
    starterTokenData: (assetIds: readonly string[]) =>
      ["onboarding", "starter-token-data", ...assetIds] as const,
    filteredTokenList: (chainId: number) =>
      ["onboarding", "filtered-token-list", chainId] as const,
    portfolioStatus: (portfolioId: string | null) =>
      ["onboarding", "portfolio-status", portfolioId] as const,
    userPortfolio: (walletAddress: string | null) =>
      ["onboarding", "user-portfolio", walletAddress] as const,
  },
  start: {
    all: ["start"] as const,
    builderTokenData: (assetIds: readonly string[]) =>
      ["start", "builder-token-data", ...assetIds] as const,
    filteredTokenList: (chainId: number) =>
      ["start", "filtered-token-list", chainId] as const,
    portfolioStatus: (portfolioId: string | null) =>
      ["start", "portfolio-status", portfolioId] as const,
    userPortfolio: (walletAddress: string | null) =>
      ["start", "user-portfolio", walletAddress] as const,
  },
  explore: {
    all: ["explore"] as const,
    topPerformingStrategies: (
      limit: number,
      minTvlUsd: number,
      period: string,
    ) =>
      [
        "explore",
        "top-performing-strategies",
        limit,
        minTvlUsd,
        period,
      ] as const,
  },
  settings: {
    all: ["settings"] as const,
    oauthConfig: ["settings", "oauth-config"] as const,
    oauthDripByWallet: (walletAddress: string | null) =>
      ["settings", "oauth", "drip", walletAddress] as const,
    oauthTwitterByWallet: (walletAddress: string | null) =>
      ["settings", "oauth", "twitter", walletAddress] as const,
    operatingPreferenceProfile: [
      "settings",
      "operating-preference-profile",
    ] as const,
    userSwapPreferences: ["settings", "user-swap-preferences"] as const,
  },
  dashboardWatchlist: {
    all: ["dashboard-watchlist"] as const,
    search: (searchTerm: string) =>
      ["dashboard-watchlist", "search", searchTerm] as const,
    equitySparklinesByKey: (
      range: string,
      tickersKey: string,
      from: string,
      to: string,
    ) =>
      [
        "dashboard-watchlist",
        "equity-sparklines",
        range,
        tickersKey,
        from,
        to,
      ] as const,
    quickSuggestionStock: (symbol: string) =>
      ["dashboard-watchlist", "quick-suggestion", "stock", symbol] as const,
    quickSuggestionStockSelection: (symbol: string) =>
      [
        "dashboard-watchlist",
        "quick-suggestion",
        "stock-selection",
        symbol,
      ] as const,
    sparklinesByKey: (range: string, assetIdsKey: string) =>
      ["dashboard-watchlist", "sparklines", range, assetIdsKey] as const,
  },
  portfolioDomain: {
    all: ["portfolio-domain"] as const,
    bootstrap: (
      sessionScopeToken: string | undefined,
      ownerAddressesCacheKey: string,
    ) =>
      [
        "portfolio-domain",
        "bootstrap",
        sessionScopeToken,
        ownerAddressesCacheKey,
      ] as const,
  },
  portfolioCreationFlow: {
    all: ["portfolio-creation-flow"] as const,
    userPortfolio: (walletAddress: string | null) =>
      ["portfolio-creation-flow", "user-portfolio", walletAddress] as const,
    filteredTokenList: (chainId: number) =>
      ["portfolio-creation-flow", "filtered-token-list", chainId] as const,
    portfolioStatus: (portfolioId: string | null) =>
      ["portfolio-creation-flow", "portfolio-status", portfolioId] as const,
  },
  directIndex: {
    catalog: ["direct-index", "catalog"] as const,
    constituents: (kind: string, id: string) =>
      ["direct-index", "constituents", kind, id] as const,
    fundamentals: (chunkKey: string) =>
      ["direct-index", "fundamentals", chunkKey] as const,
    metrics: (tickersKey: string) =>
      ["direct-index", "metrics", tickersKey] as const,
    stockSearch: (normalizedQuery: string) =>
      ["direct-index", "stock-search", normalizedQuery] as const,
    stockSuggestions: (normalizedQuery: string) =>
      ["direct-index", "stock-suggestions", normalizedQuery] as const,
    resolvedAssets: (assetCanonicalIds: readonly string[]) =>
      [
        "direct-index",
        "resolved-assets",
        [...assetCanonicalIds].sort().join(","),
      ] as const,
    preview: (signature: string) =>
      ["direct-index", "preview", signature] as const,
  },
  notifications: {
    inboxConfig: ["notifications", "inbox-config"] as const,
  },
  agentControlPlane: {
    accountOps: ["agent-control-plane", "account-ops"] as const,
    assistantArtifacts: (
      kind?: string | null,
      limit?: number | null,
      sessionId?: string | null,
    ) =>
      [
        "agent-control-plane",
        "assistant",
        "artifacts",
        kind ?? "all-kinds",
        limit ?? "default",
        sessionId ?? "all-sessions",
      ] as const,
    assistantConversation: (sessionId: string) =>
      ["agent-control-plane", "assistant", "conversation", sessionId] as const,
    assistantMemories: (
      query?: string | null,
      kind?: string | null,
      limit?: number | null,
      pinned?: boolean | null,
    ) =>
      [
        "agent-control-plane",
        "assistant",
        "memories",
        query ?? "all-queries",
        kind ?? "all-kinds",
        limit ?? "default",
        pinned ?? "all-pinned-states",
      ] as const,
    assistantSessionSearch: (
      query: string,
      limit?: number | null,
      kinds?: readonly string[] | null,
    ) =>
      [
        "agent-control-plane",
        "assistant",
        "sessions",
        "search",
        query,
        limit ?? "default",
        kinds?.join(",") ?? "all-kinds",
      ] as const,
    assistantTasks: (
      type?: string | null,
      status?: string | null,
      limit?: number | null,
    ) =>
      [
        "agent-control-plane",
        "assistant",
        "tasks",
        type ?? "all-types",
        status ?? "all-statuses",
        limit ?? "default",
      ] as const,
    assistantTask: (taskId: string) =>
      ["agent-control-plane", "assistant", "tasks", "detail", taskId] as const,
    assistantWorkspace: [
      "agent-control-plane",
      "assistant",
      "workspace",
    ] as const,
    assistantWorkspaceById: (sessionId: string) =>
      ["agent-control-plane", "assistant", "workspaces", sessionId] as const,
    assistantWorkspaces: [
      "agent-control-plane",
      "assistant",
      "workspaces",
    ] as const,
    all: ["agent-control-plane"] as const,
    identity: ["agent-control-plane", "identity"] as const,
    inbox: ["agent-control-plane", "inbox"] as const,
    inboxByFilters: (
      portfolioId?: string | null,
      status?: string | null,
      limit?: number | null,
    ) =>
      [
        "agent-control-plane",
        "inbox",
        portfolioId ?? "all-portfolios",
        status ?? "all-statuses",
        limit ?? "default",
      ] as const,
    notifications: ["agent-control-plane", "notifications"] as const,
    notificationsByFilters: (
      kind?: string | null,
      portfolioId?: string | null,
      limit?: number | null,
    ) =>
      [
        "agent-control-plane",
        "notifications",
        kind ?? "all-kinds",
        portfolioId ?? "all-portfolios",
        limit ?? "default",
      ] as const,
    search: ["agent-control-plane", "search"] as const,
    searchByQuery: (query: string, limit?: number) =>
      ["agent-control-plane", "search", query, limit ?? "default"] as const,
  },
  assetDomain: {
    all: ["asset-domain"] as const,
    assets: ["asset-domain", "assets"] as const,
    assetPrices: ["asset-domain", "asset-prices"] as const,
    overlayHydration: ["asset-domain", "overlay-hydration"] as const,
    overlayChunks: ["asset-domain", "overlay-chunks"] as const,
    overlayHydrationByKey: (key: string) =>
      ["asset-domain", "overlay-hydration", key] as const,
    overlayChunkByKey: (key: string) =>
      ["asset-domain", "overlay-chunks", key] as const,
    universeSymbolsByKey: (key: string) =>
      ["asset-domain", "universe-symbols", key] as const,
    canonicalGraphsByKey: (key: string) =>
      ["asset-domain", "canonical-graphs", key] as const,
  },
  rwaMarkets: {
    all: ["rwa-markets"] as const,
    assetGraphBySymbols: (symbolsKey: string) =>
      ["rwa-markets", "asset-graph", symbolsKey] as const,
    assetGraphHydrationByKey: (key: string) =>
      ["rwa-markets", "asset-graph-hydration", key] as const,
  },
  strategyDomain: {
    all: ["strategy-domain"] as const,
    strategyBlueprintDetails: [
      "strategy-domain",
      "strategy-blueprint-details",
    ] as const,
    strategyBlueprintPerformance: [
      "strategy-domain",
      "strategy-blueprint-performance",
    ] as const,
    strategyPerformanceBenchmarks: [
      "strategy-domain",
      "strategy-performance-benchmarks",
    ] as const,
    strategySummaries: ["strategy-domain", "strategy-summaries"] as const,
    strategyTvlContributors: [
      "strategy-domain",
      "strategy-tvl-contributors",
    ] as const,
    strategyTvlStats: ["strategy-domain", "strategy-tvl-stats"] as const,
  },
  walletIdentity: {
    all: ["wallet-identity"] as const,
    walletIdentities: ["wallet-identity", "wallet-identities"] as const,
  },
  userProfile: {
    accountPerformance: ["user-profile", "account-performance"] as const,
    accountProfiles: ["user-profile", "account-profiles"] as const,
    all: ["user-profile"] as const,
  },
  stocks: {
    all: ["stocks"] as const,
    bootstrap: ["stocks", "bootstrap"] as const,
    browse: ["stocks", "browse"] as const,
    assetUniverseList: ["stocks", "asset-universe-list"] as const,
    assetUniverseSearch: ["stocks", "asset-universe-search"] as const,
    selection: ["stocks", "selection"] as const,
    strategy: ["stocks", "strategy"] as const,
    bootstrapByKey: (key: string) => ["stocks", "bootstrap", key] as const,
    browseByKey: (key: string) => ["stocks", "browse", key] as const,
    assetUniverseListByKey: (key: string) =>
      ["stocks", "asset-universe-list", key] as const,
    assetUniverseSearchByKey: (key: string) =>
      ["stocks", "asset-universe-search", key] as const,
    assetUniverseSummariesBySymbols: (key: string) =>
      ["stocks", "asset-universe-summaries-by-symbols", key] as const,
    selectionByKey: (key: string) => ["stocks", "selection", key] as const,
    strategyById: (blueprintId: string) =>
      ["stocks", "strategy", blueprintId] as const,
    topStockMoverSparklines: (assetIdsKey: string, sessionScopeToken: string) =>
      [
        "stocks",
        "top-stock-movers",
        "sparklines",
        assetIdsKey,
        sessionScopeToken,
      ] as const,
    // Root segment intentionally differs from the other stocks.* keys: it
    // predates the factory and persisted caches key on it as-is.
    assetUniverseListInfinite: (
      assetClass: string,
      availabilityFilter: string,
      category: string | null,
      limit: number,
    ) =>
      [
        "asset-universe-list-infinite",
        assetClass,
        availabilityFilter,
        category,
        limit,
      ] as const,
  },
  symbol: {
    all: ["symbol"] as const,
    resolutionByKey: (key: string) => ["symbol", "resolution", key] as const,
    tokenizedIssuances: (assetCanonicalId: string) =>
      ["symbol", "tokenized-issuances", assetCanonicalId] as const,
    fundComposition: (ticker: string) =>
      ["symbol", "fund-composition", ticker] as const,
    fundExposures: (ticker: string) =>
      ["symbol", "fund-exposures", ticker] as const,
    corporateActions: (assetCanonicalId: string) =>
      ["symbol", "corporate-actions", assetCanonicalId] as const,
    assetLookup: (chainId: string | null, symbol: string) =>
      [
        "symbol",
        "asset-lookup",
        chainId ?? "all-supported-chains",
        symbol,
      ] as const,
    chainTokenTimeSeries: (
      assetId: string,
      range: string,
      fromSeconds: number,
      toSeconds: number,
    ) =>
      [
        "symbol",
        "chain-token-time-series",
        assetId,
        range,
        fromSeconds,
        toSeconds,
      ] as const,
  },
  cryptoMarketData: {
    all: ["crypto-market-data"] as const,
    canonicalAssetsByKey: (key: string) =>
      ["crypto-market-data", "canonical-assets", key] as const,
    canonicalAssetSearchByKey: (key: string) =>
      ["crypto-market-data", "canonical-asset-search", key] as const,
    newsByKey: (key: string) => ["crypto-market-data", "news", key] as const,
    global: ["crypto-market-data", "global"] as const,
    marketIndicators: ["crypto-market-data", "market-indicators"] as const,
    marketThemes: ["crypto-market-data", "market-themes"] as const,
    marketQuotesByKey: (key: string) =>
      ["crypto-market-data", "market-quotes", key] as const,
    trendingByKey: (key: string) =>
      ["crypto-market-data", "trending", key] as const,
    topMoversByKey: (key: string) =>
      ["crypto-market-data", "top-movers", key] as const,
    assetPlatforms: ["crypto-market-data", "asset-platforms"] as const,
    coinCategories: ["crypto-market-data", "coin-categories"] as const,
  },
  tradFiMarketData: {
    all: ["tradfi-market-data"] as const,
    fxRateByPair: (from: string, to: string) =>
      ["tradfi-market-data", "fx-rate", from, to] as const,
    fxRates: ["tradfi-market-data", "fx-rates"] as const,
    marketStatus: ["tradfi-market-data", "market-status"] as const,
    stockDetailsByTicker: (ticker: string, newsLimit?: number | null) =>
      [
        "tradfi-market-data",
        "stock-details",
        ticker,
        newsLimit ?? "default",
      ] as const,
    stockEarningsByTicker: (ticker: string, limit?: number | null) =>
      [
        "tradfi-market-data",
        "stock-earnings",
        ticker,
        limit ?? "default",
      ] as const,
    stockCatalystsByTicker: (ticker: string, limit?: number | null) =>
      [
        "tradfi-market-data",
        "stock-catalysts",
        ticker,
        limit ?? "default",
      ] as const,
    dividendsByTicker: (ticker: string) =>
      ["tradfi-market-data", "dividends", "v1", ticker] as const,
    stockFundamentalsByTicker: (ticker: string) =>
      ["tradfi-market-data", "stock-fundamentals", "v3", ticker] as const,
    stockMarketIntelligenceByTicker: (
      ticker: string,
      lookbackDays?: number | null,
      ratingsLimit?: number | null,
      insightsLimit?: number | null,
    ) =>
      [
        "tradfi-market-data",
        "stock-market-intelligence",
        "v1",
        ticker,
        lookbackDays ?? "default",
        ratingsLimit ?? "default",
        insightsLimit ?? "default",
      ] as const,
    stockBusinessSegmentsByTicker: (ticker: string) =>
      ["tradfi-market-data", "stock-business-segments", "v1", ticker] as const,
    stockRiskFactorsByTicker: (ticker: string) =>
      ["tradfi-market-data", "stock-risk-factors", "v1", ticker] as const,
    stockNewsByKey: (key: string) =>
      ["tradfi-market-data", "stock-news", key] as const,
    currencyNewsByKey: (key: string) =>
      ["tradfi-market-data", "currency-news", key] as const,
    stockMarketMoversByKey: (key: string) =>
      ["tradfi-market-data", "stock-market-movers", key] as const,
    relatedTickersByTicker: (ticker: string) =>
      ["tradfi-market-data", "related-tickers", ticker] as const,
    stockAggregateBarsByKey: (key: string) =>
      ["tradfi-market-data", "stock-aggregate-bars", key] as const,
    stockAggregateBarsBatchByKey: (key: string) =>
      ["tradfi-market-data", "stock-aggregate-bars-batch", key] as const,
    indexSnapshotsByKey: (key: string) =>
      ["tradfi-market-data", "index-snapshots", key] as const,
    sp500HeatmapByKey: (key: string) =>
      ["tradfi-market-data", "sp500-heatmap", key] as const,
    equitySectorPerformanceByKey: (key: string) =>
      ["tradfi-market-data", "equity-sector-performance", key] as const,
    tradeableAssetNewsByKey: (key: string) =>
      ["tradfi-market-data", "tradeable-asset-news", key] as const,
    stockSnapshotsByKey: (key: string) =>
      ["tradfi-market-data", "stock-snapshots", key] as const,
    forexAggregateBarsByKey: (key: string) =>
      ["tradfi-market-data", "forex-aggregate-bars", key] as const,
    commodityMarketIndicators: [
      "tradfi-market-data",
      "commodity-market-indicators",
      "v1",
    ] as const,
    upcomingIposByKey: (key: string) =>
      ["tradfi-market-data", "upcoming-ipos", key] as const,
    fundDistributionYieldsByKey: (key: string) =>
      ["tradfi-market-data", "fund-distribution-yields", key] as const,
    stablebondQuotes: ["tradfi-market-data", "stablebond-quotes"] as const,
    treasuryYieldCurve: ["tradfi-market-data", "treasury-yield-curve"] as const,
  },
  portfolio: {
    all: ["portfolio-db"] as const,
    agents: ["portfolio-control-plane", "agents"] as const,
    allocationHistoryCheckpoints: [
      "portfolio-db",
      "allocation-history-checkpoints",
    ] as const,
    approvals: ["portfolio-control-plane", "approvals"] as const,
    approvalsById: (portfolioId: string) =>
      ["portfolio-control-plane", "approvals", portfolioId] as const,
    approvalDetailById: (portfolioId: string, approvalId: string | null) =>
      [
        "portfolio-control-plane",
        "approvals",
        portfolioId,
        "detail",
        approvalId,
      ] as const,
    assetPnl: ["portfolio-db", "asset-pnl"] as const,
    assetPrices: ["portfolio-db", "asset-prices"] as const,
    assetUniverse: ["portfolio-db", "asset-universe"] as const,
    assets: ["portfolio-db", "assets"] as const,
    dashboardAssetSummaries: [
      "portfolio-db",
      "dashboard-asset-summaries",
    ] as const,
    compositionAssetDetails: (assetId: string) =>
      ["portfolio", "composition", "asset-details", assetId] as const,
    compositionTimeSeries: (
      assetId: string,
      period: string,
      fromSeconds: number,
      resolution: string,
      toSeconds: number,
    ) =>
      [
        "portfolio",
        "composition",
        "time-series",
        assetId,
        period,
        fromSeconds,
        resolution,
        toSeconds,
      ] as const,
    // Root segment predates the factory: persisted caches key connected
    // wallet balances on it as-is.
    connectedWalletBalances: (
      walletAddress: string,
      chainId: number | null,
      assetIds: readonly string[],
    ) =>
      ["connected-wallet-balances", walletAddress, chainId, assetIds] as const,
    controlPlane: ["portfolio-control-plane"] as const,
    contexts: ["portfolio-control-plane", "contexts"] as const,
    contextDetailById: (portfolioId: string, contextId: string | null) =>
      ["portfolio-control-plane", "contexts", portfolioId, contextId] as const,
    // Root segment predates the factory: deposit instructions key on it
    // as-is.
    depositInstructionsById: (portfolioId: string) =>
      ["portfolio-deposit-instructions", portfolioId] as const,
    detailBootstrapById: (
      portfolioId: string,
      historyLimit: number,
      performanceResolution?: string | null,
      positionsInSharesUiEnabled = false,
    ) =>
      [
        "portfolio-db",
        "detail-bootstrap",
        portfolioId,
        historyLimit,
        performanceResolution ?? "default",
        positionsInSharesUiEnabled,
      ] as const,
    events: ["portfolio-control-plane", "events"] as const,
    eventsById: (portfolioId: string) =>
      ["portfolio-control-plane", "events", portfolioId] as const,
    historyEvents: ["portfolio-db", "history-events"] as const,
    performancePoints: ["portfolio-db", "performance-points"] as const,
    performanceSeries: ["portfolio-db", "performance-series"] as const,
    performanceSummaries: ["portfolio-db", "performance-summaries"] as const,
    performanceWindowStats: [
      "portfolio-db",
      "performance-window-stats",
    ] as const,
    livePositions: ["portfolio-db", "live-positions"] as const,
    portfolios: ["portfolio-db", "portfolio-instances"] as const,
    portfolioInstances: ["portfolio-db", "portfolio-instances"] as const,
    positions: ["portfolio-db", "positions"] as const,
    runs: ["portfolio-control-plane", "runs"] as const,
    runsById: (portfolioId: string) =>
      ["portfolio-control-plane", "runs", portfolioId] as const,
    runExecutionDetailById: (portfolioId: string, operationId: string | null) =>
      [
        "portfolio-control-plane",
        "runs",
        portfolioId,
        "execution-detail",
        operationId,
      ] as const,
    runsWorkflowLookup: (
      portfolioId: string,
      workflowId: string | null,
      operationIds: readonly string[],
    ) =>
      [
        "portfolio-control-plane",
        "runs",
        portfolioId,
        "workflow-lookup",
        workflowId,
        operationIds,
      ] as const,
    schedules: ["portfolio-db", "schedules"] as const,
    settingsSchedule: (portfolioId: string) =>
      ["portfolio", "settings", portfolioId, "schedule"] as const,
    settingsScheduleAdvisory: (portfolioId: string) =>
      ["portfolio", "settings", portfolioId, "schedule-advisory"] as const,
    settingsSwapPreferences: (portfolioId: string) =>
      ["portfolio", "settings", portfolioId, "swap-preferences"] as const,
    scheduledFunctions: ["portfolio-db", "scheduled-functions"] as const,
    status: ["portfolio-status"] as const,
    statusById: (portfolioId: string, vmPreference: VmPreference) =>
      ["portfolio-status", portfolioId, vmPreference] as const,
    vaults: ["portfolio-db", "vaults"] as const,
    withdrawableLiveAssetsById: (portfolioId: string) =>
      ["portfolio-withdrawable-live-assets", portfolioId] as const,
    workflows: ["portfolio-db", "workflows"] as const,
    public: ["portfolio", "public"] as const,
    publicById: (portfolioId: string) =>
      ["portfolio", "public", portfolioId] as const,
    publicPerformanceById: (portfolioId: string) =>
      ["portfolio", "public", portfolioId, "performance"] as const,
    publicPerformanceSeriesById: (portfolioId: string) =>
      ["portfolio", "public", portfolioId, "performance-series"] as const,
    publicScheduleById: (portfolioId: string) =>
      ["portfolio", "public", portfolioId, "schedule"] as const,
    publicHistoryEventsById: (portfolioId: string, pageSize: number) =>
      [
        "portfolio-db",
        "history-events",
        "public",
        portfolioId,
        pageSize,
      ] as const,
  },
  dashboard: {
    all: ["portfolio-db"] as const,
    accountPerformanceSeries: [
      "platform-api",
      "strategyInstances.getAccountPerformanceSeries",
    ] as const,
    assetPrices: ["portfolio-db", "asset-prices"] as const,
    assets: ["portfolio-db", "assets"] as const,
    controlPlane: ["dashboard-control-plane"] as const,
    marketNewsAssetIcons: (tickersKey: string) =>
      ["dashboard", "market-news-asset-icons", tickersKey] as const,
    reviewQueue: ["dashboard-control-plane", "review-queue"] as const,
    portfolios: ["portfolio-db", "portfolio-instances"] as const,
    portfolioInstances: ["portfolio-db", "portfolio-instances"] as const,
    positions: ["portfolio-db", "positions"] as const,
    vaults: ["portfolio-db", "vaults"] as const,
  },
  investingAccount: {
    all: ["investing-account"] as const,
    detail: ["investing-account", "detail"] as const,
    detailById: (strategyInstanceId: string | null) =>
      ["investing-account", "detail", strategyInstanceId] as const,
    ownedStrategies: ["investing-account", "owned-strategies"] as const,
    ownedStrategiesByAddress: (ownerAddress: string | null) =>
      ["investing-account", "owned-strategies", ownerAddress] as const,
    ownedStrategiesForDashboard: (ownerAddressesCacheKey: string) =>
      [
        "investing-account",
        "owned-strategies",
        "dashboard",
        ownerAddressesCacheKey,
      ] as const,
  },
  walletExecutionDeposit: {
    all: ["wallet-execution-deposit"] as const,
    // Auth-scoped: getConfig is authenticated and returns wallet-dependent
    // eligibility (fee coverage, geo verdict) — an unscoped key would let a
    // second account in the same browser inherit the previous account's
    // cached availability for the staleTime window.
    crossmintConfig: (authScope: AuthQueryScope) =>
      withAuthQueryScope(
        ["wallet-execution-deposit", "crossmint-config"],
        authScope,
      ),
    crossmintOrder: (orderId: string | null) =>
      ["wallet-execution-deposit", "crossmint-order", orderId] as const,
    gliderBalanceAssets: (strategyInstanceId: string | null) =>
      [
        "wallet-execution-deposit",
        "glider-balance-assets",
        strategyInstanceId,
      ] as const,
    gliderBalanceSource: (
      ownerAddress: string | null,
      investingAccountIndex: string | null,
      destinationVm: string,
    ) =>
      [
        "wallet-execution-deposit",
        "glider-balance-source",
        ownerAddress,
        investingAccountIndex,
        destinationVm,
      ] as const,
    onramperSignature: (chainId: number, walletAddress: string) =>
      [
        "wallet-execution-deposit",
        "onramper-signature",
        chainId,
        walletAddress,
      ] as const,
  },
  walletExecutionIdentity: {
    all: ["wallet-execution-identity"] as const,
    balances: ["wallet-execution-identity", "balances"] as const,
    assetBalancesSlice: (walletKey: string, assetIdsKey: string) =>
      [
        "wallet-execution-identity",
        "balances",
        "asset-balances",
        walletKey,
        assetIdsKey,
      ] as const,
    balancesSlice: (
      walletKey: string,
      candidateAssetIdsKey: string,
      investingAccountKey: string,
    ) =>
      [
        "wallet-execution-identity",
        "balances",
        walletKey,
        candidateAssetIdsKey,
        investingAccountKey,
      ] as const,
  },
  workflows: {
    all: ["workflow-v2"] as const,
    detailById: (workflowId: string) =>
      ["workflow-v2", "detail", workflowId] as const,
  },
  editor: {
    all: ["editor-db"] as const,
    assetOptions: ["editor-db", "asset-options"] as const,
    snapshots: ["editor-db", "snapshots"] as const,
    updates: ["editor-db", "updates"] as const,
    assetOptionsByKey: (key: string) =>
      ["editor-db", "asset-options", key] as const,
    snapshotById: (strategyInstanceId: string) =>
      ["editor-db", "snapshot", strategyInstanceId] as const,
    updatesById: (strategyInstanceId: string) =>
      ["editor-db", "updates", strategyInstanceId] as const,
  },
} as const;

export interface AuthQueryScope {
  scopeToken: string;
  userId: string | null;
  walletAddress: string | null;
}

export interface SessionQueryScope {
  authScope: AuthQueryScope;
  scopeToken: string;
  vmPreference: VmPreference;
}

export const AUTH_SCOPE_QUERY_KEY_SEGMENT = "auth-scope" as const;
export const SESSION_SCOPE_QUERY_KEY_SEGMENT = "session-scope" as const;
export const DEFAULT_SESSION_QUERY_SCOPE_TOKEN = "guest:vm:auto";

interface AuthQueryScopeInput {
  authenticated: boolean;
  user?: unknown;
  walletAddress?: string | null;
}

interface SessionQueryScopeInput extends AuthQueryScopeInput {
  vmPreference?: unknown;
}

function asRecord(value: unknown): Record<string, unknown> | null {
  if (!value || typeof value !== "object") {
    return null;
  }

  return value as Record<string, unknown>;
}

function normalizeNonEmptyString(value: unknown): string | null {
  if (typeof value !== "string") {
    return null;
  }

  const trimmed = value.trim();
  return trimmed.length > 0 ? trimmed : null;
}

function normalizeWalletAddress(value: unknown): string | null {
  const normalized = normalizeNonEmptyString(value);
  return normalized ? normalized.toLowerCase() : null;
}

function normalizeVmPreference(value: unknown): VmPreference {
  return parseVmId(value) ?? "auto";
}

export function createAuthQueryScope(
  input: AuthQueryScopeInput,
): AuthQueryScope {
  const userRecord = asRecord(input.user);
  const userWalletRecord = asRecord(userRecord?.wallet);
  const userId = normalizeNonEmptyString(userRecord?.id);
  const walletAddress =
    normalizeWalletAddress(input.walletAddress) ??
    normalizeWalletAddress(userWalletRecord?.address);

  const scopeToken = !input.authenticated
    ? "guest"
    : walletAddress
      ? `wallet:${walletAddress}`
      : userId
        ? `user:${userId}`
        : "authed:unknown";

  return {
    scopeToken,
    userId,
    walletAddress,
  };
}

export function createUserAuthQueryScope(
  input: AuthQueryScopeInput,
): AuthQueryScope {
  const userRecord = asRecord(input.user);
  const userWalletRecord = asRecord(userRecord?.wallet);
  const userId = normalizeNonEmptyString(userRecord?.id);
  const walletAddress =
    normalizeWalletAddress(input.walletAddress) ??
    normalizeWalletAddress(userWalletRecord?.address);

  const scopeToken = !input.authenticated
    ? "guest"
    : userId
      ? `user:${userId}`
      : walletAddress
        ? `wallet:${walletAddress}`
        : "authed:unknown";

  return {
    scopeToken,
    userId,
    walletAddress,
  };
}

export function withAuthQueryScope(
  baseKey: ReadonlyArray<unknown>,
  scope: AuthQueryScope,
): ReadonlyArray<unknown> {
  return [...baseKey, AUTH_SCOPE_QUERY_KEY_SEGMENT, scope.scopeToken];
}

export function createSessionQueryScope(
  input: SessionQueryScopeInput,
): SessionQueryScope {
  const authScope = createAuthQueryScope(input);
  const vmPreference = normalizeVmPreference(input.vmPreference);

  return {
    authScope,
    scopeToken: `${authScope.scopeToken}:vm:${vmPreference}`,
    vmPreference,
  };
}

export function withSessionQueryScope(
  baseKey: ReadonlyArray<unknown>,
  scope: Pick<SessionQueryScope, "scopeToken">,
): ReadonlyArray<unknown> {
  return [...baseKey, SESSION_SCOPE_QUERY_KEY_SEGMENT, scope.scopeToken];
}

export function getAuthScopeTokenFromQueryKey(
  queryKey: ReadonlyArray<unknown>,
): string | null {
  const scopeSegmentIndex = queryKey.lastIndexOf(AUTH_SCOPE_QUERY_KEY_SEGMENT);
  if (scopeSegmentIndex < 0) {
    return null;
  }

  const scopeToken = queryKey[scopeSegmentIndex + 1];
  return typeof scopeToken === "string" ? scopeToken : null;
}

export function getSessionScopeTokenFromQueryKey(
  queryKey: ReadonlyArray<unknown>,
): string | null {
  const scopeSegmentIndex = queryKey.lastIndexOf(
    SESSION_SCOPE_QUERY_KEY_SEGMENT,
  );
  if (scopeSegmentIndex < 0) {
    return null;
  }

  const scopeToken = queryKey[scopeSegmentIndex + 1];
  return typeof scopeToken === "string" ? scopeToken : null;
}
